
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import type { BillStage } from '@/lib/stage-utils';

type CreateSourceInput = {
  stage: BillStage;
  source_type: string; // email | manual | bulk | portal | drive | sftp | api
  configuration?: Record<string, any>;
};

type UpdateSourceStatusInput = {
  id: string;
  status: 'inactive' | 'active' | 'error';
};

export const useIngestionSources = (stage?: BillStage) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const sourcesQuery = useQuery<any[]>({
    queryKey: ['ingestion-sources', user?.id, stage],
    queryFn: async () => {
      if (!user?.id) return [];

      // Use relaxed typing for non-typed table
      let query: any = (supabase.from as any)('ingest_sources')
        .select('*')
        .eq('user_id', user.id);

      if (stage) {
        query = query.contains('config', { stage });
      }

      const { data, error } = await query.order('updated_at', { ascending: false });
      if (error) throw error;

      // Map for backward compatibility: expose `stage` and `configuration`
      const mapped = (data || []).map((row: any) => ({
        ...row,
        stage: row?.config?.stage,
        configuration: row?.config ?? {},
      }));

      return mapped;
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
  });

  const createSource = useMutation({
    mutationFn: async ({ stage, source_type, configuration }: CreateSourceInput) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { error } = await (supabase.from as any)('ingest_sources').insert({
        user_id: user.id,
        name: configuration?.name ?? `${source_type} source`,
        source_type,
        config: { ...(configuration || {}), stage },
        status: 'inactive',
      });
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-sources'] });
      toast({ title: 'Source added', description: 'Ingestion source created.' });
    },
    meta: {
      onError: (error: any) => console.error('Create source failed:', error),
    },
  });

  const updateSourceStatus = useMutation({
    mutationFn: async ({ id, status }: UpdateSourceStatusInput) => {
      if (!user?.id) throw new Error('User not authenticated');
      const { error } = await (supabase.from as any)('ingest_sources')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', user.id);
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-sources'] });
      toast({ title: 'Source updated', description: 'Source status updated.' });
    },
    meta: {
      onError: (error: any) => console.error('Update source failed:', error),
    },
  });

  return {
    sources: sourcesQuery.data || [],
    isLoading: sourcesQuery.isLoading,
    error: sourcesQuery.error,
    createSource: createSource.mutateAsync,
    updateSourceStatus: updateSourceStatus.mutateAsync,
    isCreating: createSource.isPending,
    isUpdating: updateSourceStatus.isPending,
  };
};
